/**
 * RealtimeService — WebSocket-based real-time updates for multi-user sync.
 * 
 * Automatically invalidates cache and reloads data when changes are made by other users.
 * Falls back gracefully if WebSocket is not available.
 */

import { ApiClient } from './ApiClient.js';
import { CacheService } from './CacheService.js';

let ws = null;
let reconnectTimeout = null;
let pingInterval = null;
let stableTimer = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_DELAY = 1000;
const PING_INTERVAL_MS = 25000;
const STABLE_AFTER_MS = 5000;
const listeners = new Set();

export class RealtimeService {
    static isConnected() {
        return ws && ws.readyState === WebSocket.OPEN;
    }

    static connect() {
        if (typeof window === 'undefined') return;

        // Don't connect without an auth token
        const token = this.getAuthToken();
        if (!token) {
            console.log('[Realtime] No auth token, skipping WebSocket connection');
            return;
        }

        // Skip if already connected or connecting
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
            console.log('[Realtime] Already connected or connecting, skipping');
            return;
        }

        // Close previous WebSocket cleanly before opening a new one
        if (ws) {
            const oldWs = ws;
            ws = null;
            try {
                oldWs.onclose = null;
                oldWs.close(1000, 'Reconnecting');
            } catch (e) {
                // Ignore close errors
            }
        }

        this._connectWithTicket(token).catch((e) => {
            console.error('[Realtime] Failed to connect:', e);
            this.scheduleReconnect();
        });
    }

    static _wsUrl() {
        const apiBase = (ApiClient?.getBaseUrl?.() || '').replace(/\/+$/, '');
        // Prefer page origin when API is same-origin / proxied (empty base)
        if (!apiBase) {
            const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
            return `${proto}://${window.location.host}/ws`;
        }
        return `${apiBase.replace(/^http/, 'ws')}/ws`;
    }

    static async _connectWithTicket(accessToken) {
        const ticketRes = await fetch(`${ApiClient.getBaseUrl()}/api/v1/auth/ws-ticket`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
        });
        const ticketJson = await ticketRes.json().catch(() => ({}));
        const ticket = ticketJson?.data?.ticket;
        if (!ticketRes.ok || !ticket) {
            throw new Error(ticketJson?.error?.message || 'Failed to obtain WebSocket ticket');
        }

        const wsUrl = this._wsUrl();
        // UUID-only protocol — JWT cannot be a subprotocol (base64 '=' is illegal → browser 403)
        const protocol = `crm.ticket.${ticket}`;
        console.log('[Realtime] Connecting to WebSocket:', wsUrl);
        const newWs = new WebSocket(wsUrl, [protocol]);
        ws = newWs;

        newWs.onopen = () => {
            if (newWs !== ws) return;
            console.log('[Realtime] Connected');
            this._startPing(newWs);
            this.notifyListeners({ type: 'connected' });
            if (stableTimer) clearTimeout(stableTimer);
            stableTimer = setTimeout(() => {
                if (newWs === ws) reconnectAttempts = 0;
            }, STABLE_AFTER_MS);
        };

        newWs.onmessage = (event) => {
            if (newWs !== ws) return;
            const raw = typeof event.data === 'string' ? event.data : '';
            if (raw === 'pong' || raw === 'ping') return;
            try {
                const message = JSON.parse(raw);
                this.handleMessage(message);
            } catch (e) {
                console.error('[Realtime] Failed to parse message:', e);
            }
        };

        newWs.onerror = (error) => {
            if (newWs !== ws) return;
            console.error('[Realtime] WebSocket error:', error);
        };

        newWs.onclose = (event) => {
            if (newWs !== ws) return;
            console.log('[Realtime] Disconnected:', event.code, event.reason);
            this._stopPing();
            if (stableTimer) {
                clearTimeout(stableTimer);
                stableTimer = null;
            }
            this.notifyListeners({ type: 'disconnected' });
            this.scheduleReconnect();
        };
    }

    static scheduleReconnect() {
        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
        }

        let delay;
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            delay = 30000;
        } else {
            delay = RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttempts);
            reconnectAttempts++;
        }

        console.log(`[Realtime] Reconnecting in ${delay}ms (attempt ${Math.min(reconnectAttempts, MAX_RECONNECT_ATTEMPTS)}/${MAX_RECONNECT_ATTEMPTS})`);

        reconnectTimeout = setTimeout(() => {
            this.connect();
        }, delay);
    }

    static handleMessage(message) {
        console.log('[Realtime] Received:', message);

        const { type, entity, entity_id, action } = message;

        // Invalidate relevant cache entries based on the message type
        switch (type) {
            case 'deal.created':
            case 'deal.updated':
            case 'deal.stage_changed':
                // Scoped — one row changed; avoid wiping every deal-related key.
                CacheService.invalidatePattern(/^deals:list-view/);
                CacheService.invalidatePattern(/^deals:enriched/);
                CacheService.invalidatePattern(/^pages:.*\/api\/v1\/deals/);
                if (entity_id) {
                    CacheService.invalidate(`deal:${entity_id}`);
                }
                if (type === 'deal.created') {
                    CacheService.invalidatePattern(/^schedules/);
                }
                this.notifyListeners({
                    type: 'dealChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'deal.deleted':
                CacheService.invalidatePattern(/^deals/);
                CacheService.invalidatePattern(/^schedules/);
                this.notifyListeners({
                    type: 'dealChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'client.created':
            case 'client.updated':
                CacheService.invalidatePattern(/^clients:enriched/);
                CacheService.invalidatePattern(/^pages:.*\/api\/v1\/clients/);
                if (entity_id) CacheService.invalidate(`client:${entity_id}`);
                this.notifyListeners({
                    type: 'clientChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'client.deleted':
                CacheService.invalidatePattern(/^clients/);
                CacheService.invalidatePattern(/^contacts/);
                this.notifyListeners({
                    type: 'clientChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'contact.created':
            case 'contact.updated':
                CacheService.invalidatePattern(/^contacts:enriched/);
                CacheService.invalidatePattern(/^pages:.*\/api\/v1\/contacts/);
                CacheService.invalidatePattern(/^deals:contact-links/);
                if (entity_id) CacheService.invalidate(`contact:${entity_id}`);
                this.notifyListeners({
                    type: 'contactChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'contact.deleted':
                CacheService.invalidatePattern(/^contacts/);
                CacheService.invalidatePattern(/^deals:contact-links/);
                this.notifyListeners({
                    type: 'contactChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'user.created':
            case 'user.updated':
            case 'user.deleted':
                CacheService.invalidatePattern(/^users/);
                import('../reference/ReferenceDataStore.js')
                    .then(({ ReferenceDataStore }) => {
                        void ReferenceDataStore.ensureUsersLookup({ force: true });
                        void ReferenceDataStore.ensureUsersAssignable({ force: true });
                    })
                    .catch(() => {});
                this.notifyListeners({ type: 'userChanged', action, entity_id });
                break;

            case 'schedule.created':
            case 'schedule.updated':
            case 'schedule.deleted':
            case 'schedule.paid':
            case 'schedule.issued':
                CacheService.invalidatePattern(/^schedules/);
                this.notifyListeners({
                    type: 'scheduleChanged',
                    action,
                    entity_id,
                    eventType: type,
                    data: message.data || null,
                });
                break;

            case 'activity.created':
            case 'activity.updated':
                CacheService.invalidatePattern(/^activities/);
                this.notifyListeners({ type: 'activityChanged', action, entity_id, entity, data: message.data });
                break;

            case 'notification':
                {
                    const data = message.data || {};
                    const target = message.target_user_id || data.target_user_id || null;
                    const t = data.type || data.notification_type || message.notification_type || '';
                    if (t === 'task_assigned') {
                        import('../ui/shared/ToastManager.js').then(m => { try { m.ToastManager.success('Task assigned', data.title || 'New task assigned to you'); } catch {} }).catch(()=>{});
                    } else if (t === 'mention') {
                        import('../ui/shared/ToastManager.js').then(m => { try { m.ToastManager.show({ title: 'Mention', message: data.title || 'You were mentioned', tone: 'info' }); } catch {} }).catch(()=>{});
                    }
                    try {
                        const currentToken = this.getAuthToken();
                        let currentUserId = null;
                        try { if (currentToken) currentUserId = JSON.parse(atob(currentToken.split('.')[1])).sub; } catch {}
                        if (target && currentUserId && String(target) !== String(currentUserId)) {
                            const storedId = sessionStorage.getItem('quickcrm_session') ? JSON.parse(sessionStorage.getItem('quickcrm_session') || '{}')?.userId : null;
                            const fallbackId = storedId || currentUserId;
                            if (String(target) !== String(fallbackId)) {
                                window.dispatchEvent(new CustomEvent('realtime:update', { detail: message }));
                                return;
                            }
                        }
                    } catch {}
                    window.dispatchEvent(new CustomEvent('notification:received', { detail: data }));
                    window.dispatchEvent(new CustomEvent('realtime:update', { detail: message }));
                }
                return;

            case 'connected':
            case 'pong':
            case 'ping':
                return;

            default:
                console.warn('[Realtime] Unknown message type:', type);
        }

        // Dispatch custom event for UI components to react
        window.dispatchEvent(new CustomEvent('realtime:update', { 
            detail: message 
        }));
    }

    static disconnect() {
        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            reconnectTimeout = null;
        }
        this._stopPing();
        if (stableTimer) {
            clearTimeout(stableTimer);
            stableTimer = null;
        }

        if (ws) {
            ws.close(1000, 'Client disconnecting');
            ws = null;
        }

        reconnectAttempts = 0;
    }

    static subscribe(callback) {
        listeners.add(callback);
        return () => listeners.delete(callback);
    }

    static notifyListeners(message) {
        listeners.forEach(callback => {
            try {
                callback(message);
            } catch (e) {
                console.error('[Realtime] Listener error:', e);
            }
        });
    }

    static send(message) {
        if (!this.isConnected()) {
            console.warn('[Realtime] Cannot send message, not connected');
            return false;
        }

        try {
            ws.send(JSON.stringify(message));
            return true;
        } catch (e) {
            console.error('[Realtime] Failed to send message:', e);
            return false;
        }
    }
    
    static getAuthToken() {
        if (ApiClient?.getToken) {
            const token = ApiClient.getToken();
            if (token) return token;
        }
        return null;
    }

    static _startPing(socket) {
        this._stopPing();
        pingInterval = setInterval(() => {
            if (!socket || socket !== ws || socket.readyState !== WebSocket.OPEN) {
                this._stopPing();
                return;
            }
            try {
                socket.send('ping');
            } catch (e) {
                this._stopPing();
            }
        }, PING_INTERVAL_MS);
    }

    static _stopPing() {
        if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
        }
    }
}

// Auto-connect on page load
if (typeof window !== 'undefined') {
    const tryConnect = () => {
        const token = RealtimeService.getAuthToken();
        if (token) {
            RealtimeService.connect();
        } else {
            // Retry once token is available (e.g. after login redirect)
            const retryTimer = setInterval(() => {
                if (RealtimeService.getAuthToken()) {
                    clearInterval(retryTimer);
                    RealtimeService.connect();
                }
            }, 200);
            // Stop retrying after 30s to avoid infinite polling
            setTimeout(() => clearInterval(retryTimer), 30000);
        }
    };

    // Connect after a short delay to let the app initialize
    setTimeout(tryConnect, 1000);

    // Reconnect on page visibility change (user returns to tab)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && !RealtimeService.isConnected()) {
            console.log('[Realtime] Page visible, reconnecting...');
            RealtimeService.connect();
        }
    });

    // Disconnect on page unload
    window.addEventListener('beforeunload', () => {
        RealtimeService.disconnect();
    });
}